import {useEffect, useRef, useState} from "react";
import {PersonGenomeBasic} from "../../../services/interface/query/person";

interface UsePersonPositionParams {
    person?: PersonGenomeBasic | null;
    relationship?: string;
    personRefList: any[];
    setPersonRefList: React.Dispatch<React.SetStateAction<any[]>>;
    containerRef: React.RefObject<HTMLDivElement>;
    genomeRef: React.RefObject<HTMLDivElement>;
    width?: string;
}

export function usePersonPosition({
    person,
    relationship,
    personRefList,
    setPersonRefList,
    genomeRef,
    containerRef,
    width,
}: UsePersonPositionParams) {
    const personGenomeRef = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    const [position, setPosition] = useState<any | undefined>();

    function calculatePosition() {
        if (!person || !personGenomeRef.current || !genomeRef.current) return null;
        const containerRect = genomeRef.current.getBoundingClientRect();
        const personRect = personGenomeRef.current.getBoundingClientRect();
        return {
            x: personRect.x - containerRect.x,
            y: personRect.y - containerRect.y,
            height: personRect.height,
            width: personRect.width,
        };
    }

    function findIndex(list: any[]) {
        return list.findIndex(item => item.identification === person?.identification && item.relationship === relationship);
    }

    function saveRef(index: number, x: number, y: number, height: number, width: number) {
        const refData = {
            identification: person?.identification,
            relationship: relationship,
            x, y, height, width,
            entry: false,
        };
        setPosition({...refData, index: index + 1});
        if (index === -1) {
            setPersonRefList(prevList => [...prevList, refData]);
        } else {
            setPersonRefList(prevList => {
                const newList = [...prevList];
                newList[index] = refData;
                return newList;
            });
        }
    }

    function syncPosition(list: any[]) {
        const pos = calculatePosition();
        if (pos) {
            const index = findIndex(list);
            saveRef(index, pos.x, pos.y, pos.height, pos.width);
        }
    }

    useEffect(() => {
        if (active) {
            syncPosition(personRefList);
            setActive(false);
        }
    }, [active]);

    useEffect(() => {
        syncPosition(personRefList);

        const handleResize = () => setActive(true);
        const handleScroll = () => setActive(true);

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll);
        };
    }, [containerRef, genomeRef, personGenomeRef, width]);

    function updateView(value: boolean) {
        if (position && position?.index && personRefList) {
            setPersonRefList(prevList => {
                const newList = [...prevList];
                newList[position.index - 1] = {...position, entry: value};
                return newList;
            });
        }
    }

    return {personGenomeRef, updateView};
}
